import { TooltipTrigger } from '@radix-ui/react-tooltip';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { FiDownload, FiFile } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useDebounce } from 'use-debounce';

import { Button } from '@/components/atoms/Button';
import {
	Tooltip,
	TooltipArrow,
	TooltipContent,
	TooltipPortal,
	TooltipProvider,
} from '@/components/atoms/ToolTip';
import SearchInput from '@/components/molecules/SearchInput';
import { SuricattaTrainingCard } from '@/components/molecules/SuricattaTrainingCard';
import ViewDataWrapper from '@/components/molecules/ViewDataWrapper';
import api from '@/services/apiSgft';
import { SuricattaTrainingData } from '@/types/SuricattaTrainingData';
import { downloadFile } from '@/utils/downloadFile';

import ErrorIcon from '../../assets/icons/error.svg';

const SuricattaTraining = () => {
	const [
		isExportingSuricattaTrainingData,
		setIsExportingSuricattaTrainingData,
	] = useState(false);
	const [
		isExportingThirdPartyTrainingData,
		setIsExportingThirdPartyTrainingData,
	] = useState(false);
	const [trainingName, setTrainingName] = useState('');
	const [debouncedTrainingName] = useDebounce(trainingName, 500);
	const getSuricattaTrainingsData = async () => {
		try {
			const response = await api.get('/suricatta-training/all', {
				params: {
					name: debouncedTrainingName,
				},
			});
			return response.data as SuricattaTrainingData[];
		} catch (e) {
			toast.error('Erro ao carregar os treinamentos!', {
				theme: 'colored',
				toastId: 'error',
			});
		}
	};

	const handleExportSuricattaTrainings = async () => {
		setIsExportingSuricattaTrainingData(true);
		await downloadFile(
			'extraction/suricatta-training',
			'treinamentos-suricatta',
		);
		setIsExportingSuricattaTrainingData(false);
	};

	const handleExportThirdPartyTrainings = async () => {
		setIsExportingThirdPartyTrainingData(true);
		await downloadFile(
			'extraction/suricatta-training-control',
			'treinamentos-terceiros',
		);
		setIsExportingThirdPartyTrainingData(false);
	};

	const { data, isLoading, isError } = useQuery(
		['get-all-suricatta-trainings-data', debouncedTrainingName],
		getSuricattaTrainingsData,
	);

	const EmptyState = () => {
		return (
			<div className="h-full w-[95%] px-[1%]">
				<div className="flex h-full w-full flex-col items-center justify-center rounded-md">
					<img src={ErrorIcon} alt="Ícone de erro" className="w-24" />
					<div className="flex flex-col items-center text-center">
						Nenhum treinamento encontrado!
					</div>
				</div>
			</div>
		);
	};
	return (
		<div className="relative flex h-[calc(100%-3rem)] max-h-screen w-full flex-col items-start">
			<div className="absolute -top-8 right-28 flex items-center">
				<div className="flex h-[2rem] w-6 items-center justify-between rounded-lg p-4 text-[13px]">
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger>
								<Button
									className="p-2 hover:opacity-90"
									disabled={isExportingThirdPartyTrainingData}
									isLoading={isExportingThirdPartyTrainingData}
									variant={'destructive'}
								>
									<FiDownload
										onClick={handleExportThirdPartyTrainings}
										size={16}
									/>
								</Button>
							</TooltipTrigger>
							<TooltipPortal>
								<TooltipContent
									className="bg-white"
									arrowPadding={50}
									sideOffset={1}
								>
									<TooltipArrow className="h-3 w-6 fill-white" />
									<p>Download de Treinamentos por Terceiros</p>
								</TooltipContent>
							</TooltipPortal>
						</Tooltip>
					</TooltipProvider>
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger>
								<Button
									className="p-2 hover:opacity-90"
									disabled={isExportingSuricattaTrainingData}
									isLoading={isExportingSuricattaTrainingData}
									variant={'destructive'}
									onClick={handleExportSuricattaTrainings}
								>
									<FiFile size={16} />
								</Button>
							</TooltipTrigger>
							<TooltipPortal>
								<TooltipContent
									className="bg-white"
									arrowPadding={50}
									sideOffset={1}
								>
									<TooltipArrow className="h-3 w-6 fill-white" />
									<p>Download DE/PARA Treinamentos Suricatta e SGFT</p>
								</TooltipContent>
							</TooltipPortal>
						</Tooltip>
					</TooltipProvider>
				</div>
			</div>
			<div>
				<div className="mt-4 flex w-full justify-between overflow-hidden">
					<div className="mb-2 px-3 font-graphie text-2xl font-bold text-primary">
						Treinamentos Suricatta
					</div>
				</div>
				<div className="w-full px-5">
					<div className="w-full rounded-md bg-white p-4">
						<SearchInput
							placeholder="Pesquisar por treinamento"
							autoFocus
							className="border-white"
							value={trainingName}
							onChange={(e) => setTrainingName(e.target.value)}
						/>
					</div>
				</div>
				<ViewDataWrapper isLoading={isLoading} isError={isError}>
					<div className="custom-scrollbar flex h-[80vh] w-full flex-col gap-4 overflow-auto rounded-md px-5">
						{data?.length ? (
							data.map((el) => {
								return (
									<SuricattaTrainingCard key={el.id} suricattaTraining={el} />
								);
							})
						) : (
							<EmptyState />
						)}
					</div>
				</ViewDataWrapper>
			</div>
		</div>
	);
};

export default SuricattaTraining;
